<script setup>
import { AbcFloatingContainer, AbcHeading } from "@ordbogen-npm/abc-dsk";
import * as Sentry from "@sentry/vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import TgIconButton from "@/abc-dsk/components/IconButton/TgIconButton.vue";
import TgMenu from "@/abc-dsk/components/Menu/TgMenu.vue";
import TgModal from "@/abc-dsk/components/Modal/TgModal.vue";
import { getErrorMessage, withReplicationDelay } from "@/common/api/utils";
import VocabService from "@/common/api/vocab.service";
import { GameTypeEnum } from "@/common/composables/use-vocab-quiz";
import NotificationService from "@/common/notification.service";
import { WordListType } from "@/common/types/vocab";
import { mapFunctionSpecificTexts, mapLanguageSpecificTexts } from "@/common/utils";
import ActionWithTooltip from "@/components/ActionWithTooltip.vue";
import IconGreenCheckmark from "@/components/icons/green/IconGreenCheckmark.vue";
import IconCopy from "@/components/icons/IconCopy.vue";
import IconInvite from "@/components/icons/IconInvite.vue";
import IconPlus from "@/components/icons/IconPlus.vue";
import LottieAnimation from "@/components/LottieAnimation.vue";
import DuplicateLemmaWarningModal from "@/components/modals/vocabularyTrainer/DuplicateLemmaWarningModal.vue";
import ExerciseItem from "@/components/vocabularyTrainer/ExerciseItem.vue";
import WordListItemGrid from "@/components/vocabularyTrainer/WordListItemGrid.vue";
import WordListMenu from "@/components/vocabularyTrainer/WordListMenu.vue";
import WordListTranslationsSearchBar from "@/components/vocabularyTrainer/WordListTranslationsSearchBar.vue";
import { i18n } from "@/i18n";
import { useAuthStore, useSettingsStore } from "@/stores";

// General framework stuff
const { t } = i18n.global;

const authStore = useAuthStore();
const settingsStore = useSettingsStore();

const router = useRouter();
const { params } = useRoute();

// Pinia
const { user, userIsTeacher } = storeToRefs(authStore);

// Function-specific texts
const functionSpecificTexts = computed(() => {
  const targetLanguage =
    settingsStore.activeLanguageGroup === "english"
      ? t("vocab.languages.english")
      : t("vocab.languages.german");

  return mapFunctionSpecificTexts(
    {
      exerciseSectionDescription: {
        student: t("vocab.vocabList.exerciseSectionDescription.student"),
        teacher: t("vocab.vocabList.exerciseSectionDescription.teacher")
      },
      trueFalseDescription: {
        student: t("vocab.vocabList.exerciseTypes.trueFalse.description.student"),
        teacher: t("vocab.vocabList.exerciseTypes.trueFalse.description.teacher")
      },
      multipleChoiceDescription: {
        student: t("vocab.vocabList.exerciseTypes.multipleChoice.description.student"),
        teacher: t("vocab.vocabList.exerciseTypes.multipleChoice.description.teacher")
      },
      writingDescription: {
        student: t("vocab.vocabList.exerciseTypes.writing.description.student"),
        teacher: t("vocab.vocabList.exerciseTypes.writing.description.teacher")
      },
      matchingPairsDescription: {
        student: t("vocab.vocabList.exerciseTypes.matchingPairs.description.student"),
        teacher: t("vocab.vocabList.exerciseTypes.matchingPairs.description.teacher", {
          language: targetLanguage
        })
      },
      dragDropDescription: {
        student: t("vocab.vocabList.exerciseTypes.dragDrop.description.student"),
        teacher: t("vocab.vocabList.exerciseTypes.dragDrop.description.teacher")
      },
      easyMixDescription: {
        student: t("vocab.vocabList.exerciseTypes.easyMix.description.student"),
        teacher: t("vocab.vocabList.exerciseTypes.easyMix.description.teacher", {
          trueFalse: languageSpecificTexts.value.trueFalseTitle,
          multipleChoice: languageSpecificTexts.value.multipleChoiceTitle,
          matchingPairs: languageSpecificTexts.value.matchingPairsTitle
        })
      },
      hardMixDescription: {
        student: t("vocab.vocabList.exerciseTypes.hardMix.description.student"),
        teacher: t("vocab.vocabList.exerciseTypes.hardMix.description.teacher", {
          matchingPairs: languageSpecificTexts.value.matchingPairsTitle,
          dragDrop: languageSpecificTexts.value.dragDropTitle,
          writing: languageSpecificTexts.value.writingTitle
        })
      },
      exerciseButtonLabel: {
        student: t("vocab.vocabList.exerciseButton.student"),
        teacher: t("vocab.vocabList.exerciseButton.teacher")
      }
    },
    user.value.function
  );
});

// Language-specific texts
const languageSpecificTexts = computed(() =>
  mapLanguageSpecificTexts(
    {
      trueFalseTitle: {
        english: t("vocab.vocabList.exerciseTypes.trueFalse.title.english"),
        german: t("vocab.vocabList.exerciseTypes.trueFalse.title.german")
      },
      multipleChoiceTitle: {
        english: t("vocab.vocabList.exerciseTypes.multipleChoice.title.english"),
        german: t("vocab.vocabList.exerciseTypes.multipleChoice.title.german")
      },
      writingTitle: {
        english: t("vocab.vocabList.exerciseTypes.writing.title.english"),
        german: t("vocab.vocabList.exerciseTypes.writing.title.german")
      },
      matchingPairsTitle: {
        english: t("vocab.vocabList.exerciseTypes.matchingPairs.title.english"),
        german: t("vocab.vocabList.exerciseTypes.matchingPairs.title.german")
      },
      dragDropTitle: {
        english: t("vocab.vocabList.exerciseTypes.dragDrop.title.english"),
        german: t("vocab.vocabList.exerciseTypes.dragDrop.title.german")
      },
      easyMixTitle: {
        english: t("vocab.vocabList.exerciseTypes.easyMix.title.english"),
        german: t("vocab.vocabList.exerciseTypes.easyMix.title.german")
      },
      hardMixTitle: {
        english: t("vocab.vocabList.exerciseTypes.hardMix.title.english"),
        german: t("vocab.vocabList.exerciseTypes.hardMix.title.german")
      }
    },
    settingsStore.activeLanguageGroup
  )
);

// Directly component-related
const wordList = reactive({
  authorId: null,
  name: "",
  language: "",
  shareCode: "",
  words: [],
  type: null
});

const isLoaded = computed(() => wordList.shareCode);

const isListPublic = computed(() => wordList.type === WordListType.Public);
const isListOwner = computed(() => wordList.authorId === user.value.id);

const isLocked = computed(() => {
  if (isListPublic.value && userIsTeacher.value) {
    return false;
  }

  if (isListOwner.value) {
    return false;
  }

  return !wordList.words.every((word) => word.openedAt);
});

const isLockedOverlayShown = ref(false);

onMounted(() => {
  if (isLocked.value) {
    isLockedOverlayShown.value = true;
  }
});

watch(
  () => settingsStore.activeLanguageGroup,
  (languageGroup) => {
    if (languageGroup !== wordList.language) {
      router.push({ name: "vocab.home" });
    }
  }
);

watch(isLocked, (value) => {
  if (value) {
    isLockedOverlayShown.value = true;
  }
});

const hideBlockingOverlay = () => {
  setTimeout(() => {
    isLockedOverlayShown.value = false;
  }, 1000);
};

const unlockWord = async (wordId) => {
  try {
    const updatedWord = (await VocabService.openWord(params.shareCode, wordId)).data.data;
    const word = wordList.words.find((w) => w.id === wordId);

    word.openedAt = updatedWord.openedAt;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        "vocab.unlock.word": "issue"
      }
    });
    NotificationService.error(t("vocab.vocabList.wordListItem.unlockErrorToast"));
  }
};

const deleteWord = async (wordId) => {
  try {
    const word = (await VocabService.deleteWord(params.shareCode, wordId)).data.data;
    wordList.words = wordList.words.filter((w) => w.id !== wordId);
    const lemma = word.lemma;
    NotificationService.success(t("vocab.vocabList.wordListItem.toast", { lemma }));
  } catch (error) {
    NotificationService.error(t("vocab.vocabList.wordListItem.deleteWordError"));
  }
};

const updateWordList = async () => {
  const list = (await VocabService.getList(params.shareCode)).data.data;
  Object.assign(wordList, { ...list });
};
onMounted(async () => {
  try {
    await updateWordList();
  } catch (error) {
    return router.push({
      name: "vocab.home"
    });
  }
});

const setName = (value) => {
  wordList.name = value;
};

const wordCount = computed(() => wordList.words.length ?? 0);

const sortedWords = computed(() =>
  wordList.words
    .map((w) => ({
      ...w,
      deletable: isListOwner.value,
      locked: w.openedAt || userIsTeacher.value ? false : true
    }))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
);

/** @param {GameTypeEnum} gameType */
const onQuizButtonClick = async (gameType) => {
  try {
    const { code } = (
      await withReplicationDelay(() => VocabService.createQuiz(params.shareCode, gameType))
    ).data.data;

    router.push({ name: "vocab.quiz", params: { shareCode: params.shareCode, quizCode: code } });
  } catch (error) {
    if (getErrorMessage(error) === "invalid_game_type") {
      NotificationService.error(t("api.invalid_game_type"));
    } else if (getErrorMessage(error) === "game_type_not_available") {
      NotificationService.error(t("api.game_type_not_available"));
    }
  }
};

const state = reactive({
  isDuplicateLemmaWarningModalOpen: false,
  duplicateLemmaWarningWord: {},
  isShareCodeModal: false,
  shareCodeCopySuccess: false,
  showSearch: false,
  hasBackDrop: false
});

const openShareCodeModal = () => {
  state.isShareCodeModal = true;
};

const onCloseShareCodeModal = () => {
  state.isShareCodeModal = false;
  state.shareCodeCopySuccess = false;
};

const onShareCodeCopy = async () => {
  try {
    const { href } = router.resolve({
      name: "vocab.home",
      query: {
        code: wordList.shareCode,
        name: encodeURIComponent(wordList.name)
      }
    });

    const absoluteURL = new URL(href, window.location.href).href;

    await navigator.clipboard.writeText(absoluteURL);
    state.shareCodeCopySuccess = true;
  } catch (error) {
    // Ignore
  }
};

const addWord = async (event) => {
  // Prevent adding duplicates
  // This could potentially be too aggressive, and if it is, we should check lemmaId + translationId instead.
  const existingIndex = wordList.words.findIndex(
    (w) => w.lemma === event.lemma && w.translation === event.translation
  );

  if (existingIndex !== -1) {
    return;
  }

  // Show a warning and prevent adding duplicate lemmas (e.g. a different translation)
  const existingLemmaIndex = wordList.words.findIndex(
    (w) => w.lemma === event.lemma && w.lemmaWordclass.english === event.lemmaWordclass.english
  );
  if (existingLemmaIndex !== -1) {
    state.duplicateLemmaWarningWord = event;
    state.isDuplicateLemmaWarningModalOpen = true;
    return;
  }

  const newEntity = (
    await VocabService.addWord(params.shareCode, event.lemmaId, event.translationId)
  ).data.data;

  wordList.words.push(newEntity);
};

const setBackdrop = (value) => {
  state.hasBackDrop = value;
};
</script>

<template>
  <main class="px-0 mb-5 vocab-list-overview">
    <div
      class="vocab-list-overview-backdrop"
      :class="{ 'vocab-list-overview-backdrop--active': state.hasBackDrop }"
    ></div>
    <Transition name="fade">
      <AbcFloatingContainer v-if="isLoaded" class="container">
        <section class="row vocab-list-overview__section">
          <div class="col-6 col-lg-8 d-flex align-items-center">
            <AbcHeading tag="h1" size="lg" color="black">
              {{ wordList.name }}
            </AbcHeading>
            <TgMenu v-if="isListOwner" origin="right" :offset-y="5" button-class="ml-2">
              <WordListMenu
                v-if="wordList.shareCode"
                :share-code="wordList.shareCode"
                :list-name="wordList.name"
                @edit-success="setName($event)"
              />
            </TgMenu>
          </div>
          <div
            v-if="isListOwner"
            class="col-6 col-lg-4 d-flex justify-content-end align-items-center"
          >
            <div>
              <TgButton size="sm" size-md="md" color="secondary" @click="openShareCodeModal()">
                <span>{{ t("vocab.vocabList.shareCodeButton") }}</span>
                <IconInvite class="ml-2" />
              </TgButton>
              <TgModal :is-open="state.isShareCodeModal" @close="onCloseShareCodeModal()">
                <template #modal-header>
                  <span>{{ t("vocab.vocabList.shareCodeModalTitle") }}</span>
                </template>
                <div class="row share-code-modal">
                  <div class="col-12 text-center">
                    <p class="share-code-modal__label">
                      {{ t("vocab.vocabList.shareCodeModalLabel") }}
                    </p>
                    <p class="share-code-modal__code">{{ wordList.shareCode }}</p>
                  </div>
                  <div class="col-12 text-center">
                    <ActionWithTooltip :show-success="state.shareCodeCopySuccess">
                      <template #element>
                        <TgButton full-width @click="onShareCodeCopy()">
                          <IconCopy class="share-code-modal__copy-icon" role="presentation" />
                          <span>{{ t("vocab.vocabList.shareCodeModalButton") }}</span>
                        </TgButton>
                      </template>
                      <template #actionInitial>{{ t("vocab.vocabList.copy.copy") }}</template>
                      <template #actionSuccess>
                        <IconGreenCheckmark class="share-code-modal__checkmark-icon" />
                        <span>{{ t("article.actions.copy.copied") }}</span>
                      </template>
                    </ActionWithTooltip>
                  </div>
                </div>
              </TgModal>
            </div>
          </div>
        </section>
        <section class="row vocab-list-overview__section">
          <div v-if="isListOwner" class="vocab-list-overview__search-wrapper">
            <div class="d-flex align-items-center">
              <TgIconButton
                size="lg"
                variant="solid"
                aria-labelledby="show-search-icon-label"
                @click="state.showSearch = !state.showSearch"
              >
                <IconPlus
                  class="show-search-icon"
                  :class="{ 'show-search-icon--rotated': state.showSearch }"
                  role="presentation"
                />
              </TgIconButton>
            </div>
            <div>
              <p
                v-if="!state.showSearch"
                id="show-search-icon-label"
                class="vocab-list-overview__search-text"
              >
                {{ t("vocab.vocabList.addWord") }}
              </p>
              <WordListTranslationsSearchBar
                v-else
                :language="wordList.language"
                @add-word="addWord($event)"
                @set-backdrop="setBackdrop($event)"
              />
            </div>
          </div>
          <WordListItemGrid
            v-if="wordList.shareCode"
            class="vocab-list-overview__word-grid"
            :class="{ 'space-top': wordList.words.length > 0 && isListOwner }"
            :items="sortedWords"
            :md="3"
            :lg="5"
            @unlock-word="unlockWord($event)"
            @delete-word="deleteWord($event)"
          />
        </section>
        <section class="row vocab-list-overview__section">
          <div
            class="col-12"
            :class="{ 'vocab-list-overview__element-locked': isLockedOverlayShown }"
            :inert="isLockedOverlayShown"
          >
            <div class="mb-3">
              <AbcHeading tag="h1" size="lg" color="black">
                {{ $t("vocab.vocabList.exerciseSectionTitle") }}
              </AbcHeading>
              <p class="vocab-list-overview__exercises-description">
                {{ functionSpecificTexts.exerciseSectionDescription }}
              </p>
            </div>
          </div>
          <div
            class="col-12"
            :class="{ 'vocab-list-overview__element-locked': isLockedOverlayShown }"
            :inert="isLockedOverlayShown"
          >
            <div class="row">
              <div class="col exercises-grid">
                <ExerciseItem
                  icon="/assets/img/vocab/true-false.png"
                  :name="languageSpecificTexts.trueFalseTitle"
                  :description="functionSpecificTexts.trueFalseDescription"
                  :button-disabled="wordCount < 2"
                  :button-label="functionSpecificTexts.exerciseButtonLabel"
                  @button-click="onQuizButtonClick(GameTypeEnum.TrueFalse)"
                />
                <ExerciseItem
                  icon="/assets/img/vocab/multiple-choice.png"
                  :name="languageSpecificTexts.multipleChoiceTitle"
                  :description="functionSpecificTexts.multipleChoiceDescription"
                  :button-disabled="wordCount < 3"
                  :button-label="functionSpecificTexts.exerciseButtonLabel"
                  @button-click="onQuizButtonClick(GameTypeEnum.MultipleChoice)"
                />
                <ExerciseItem
                  icon="/assets/img/vocab/writing.png"
                  :name="languageSpecificTexts.writingTitle"
                  :description="functionSpecificTexts.writingDescription"
                  :button-disabled="wordCount < 1"
                  :button-label="functionSpecificTexts.exerciseButtonLabel"
                  @button-click="onQuizButtonClick(GameTypeEnum.Writing)"
                />
                <ExerciseItem
                  icon="/assets/img/vocab/matching-pairs.png"
                  :name="languageSpecificTexts.matchingPairsTitle"
                  :description="functionSpecificTexts.matchingPairsDescription"
                  :button-disabled="wordCount < 3"
                  :button-label="functionSpecificTexts.exerciseButtonLabel"
                  @button-click="onQuizButtonClick(GameTypeEnum.MatchingPairs)"
                />
                <ExerciseItem
                  icon="/assets/img/vocab/drag-drop.png"
                  :name="languageSpecificTexts.dragDropTitle"
                  :description="functionSpecificTexts.dragDropDescription"
                  :button-disabled="wordCount < 1"
                  :button-label="functionSpecificTexts.exerciseButtonLabel"
                  @button-click="onQuizButtonClick(GameTypeEnum.DragDrop)"
                />
                <ExerciseItem
                  icon="/assets/img/vocab/easy-mix.png"
                  :name="languageSpecificTexts.easyMixTitle"
                  :description="functionSpecificTexts.easyMixDescription"
                  :button-disabled="wordCount < 3"
                  :button-label="functionSpecificTexts.exerciseButtonLabel"
                  @button-click="onQuizButtonClick(GameTypeEnum.EasyMix)"
                />
                <ExerciseItem
                  icon="/assets/img/vocab/hard-mix.png"
                  :name="languageSpecificTexts.hardMixTitle"
                  :description="functionSpecificTexts.hardMixDescription"
                  :button-disabled="wordCount < 3"
                  :button-label="functionSpecificTexts.exerciseButtonLabel"
                  @button-click="onQuizButtonClick(GameTypeEnum.HardMix)"
                />
              </div>
            </div>
          </div>
          <Transition name="fade">
            <div v-if="isLockedOverlayShown" class="vocab-list-overview__blocking-overlay">
              <LottieAnimation
                path="padlock"
                :play="!isLocked"
                :speed="0.66"
                @complete="hideBlockingOverlay()"
              />

              <p class="vocab-list-overview__blocking-overlay-label">
                <span v-if="isLocked">{{ t("vocab.vocabList.unlockAllWords") }}</span>

                <!-- Avoid animation jumping because text is gone -->
                <span>&nbsp;</span>
              </p>
            </div>
          </Transition>
        </section>
      </AbcFloatingContainer>
    </Transition>

    <DuplicateLemmaWarningModal
      :is-open="state.isDuplicateLemmaWarningModalOpen"
      :word="state.duplicateLemmaWarningWord"
      @close="state.isDuplicateLemmaWarningModalOpen = false"
    />
  </main>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.vocab-list-overview {
  &__search-wrapper {
    display: grid;
    position: relative;
    z-index: 6;
    grid-template-columns: 48px 1fr;
    grid-template-rows: 48px;
    width: 100%;
    gap: 16px;
  }

  &__element-locked {
    filter: blur(5px) grayscale(1);
    pointer-events: none;
  }

  &__blocking-overlay {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 100%;
    padding: 50px 15px;
    background-color: rgba(255, 255, 255, 0.66);
    gap: 15px;

    #media-breakpoint-up(md, {
      padding: 15px;
      justify-content: center;
    });
  }

  &__blocking-overlay-label {
    margin-top: -90px;
    font-size: 1.125rem;
    font-weight: 400;
    line-height: 1.75rem;
  }
}

.vocab-list-overview__section {
  position: relative;
  padding: 20px 16px 18px 16px;
  border-bottom: 1px solid rgb(@theme-colors[@grey][50]);

  &:last-of-type {
    border: none;
  }

  @md-rules: {
    padding: 36px 30px;
  };

  #media-breakpoint-up(md, @md-rules);
}

.vocab-list-overview__word-grid {
  width: 100%;
}

.vocab-list-overview__exercises-description {
  margin-bottom: 0;
  color: rgb(@theme-colors[@grey][base]);
}

.vocab-list-overview-backdrop {
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: background-color 125ms ease-in-out;

  &--active {
    z-index: 5;
    width: 100%;
    height: 100%;
    background-color: rgba(@theme-colors[@secondary][10], 0.6);
  }
}

.vocab-list-overview__search-text {
  font-size: 1.125rem;
  line-height: 3rem;
}

.space-top {
  margin-top: 36px;
}

.share-code-modal__label {
  margin-bottom: 3px;
  color: rgb(@theme-colors[@grey][base]);
  font-size: 1rem;
  line-height: 1.5rem;
}

.share-code-modal__code {
  margin-bottom: 28px;
  color: @colors[@black];
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.75rem;
}

.share-code-modal__copy-icon {
  width: 16px;
  height: 14px;
  margin-right: 10px;
}

.share-code-modal__checkmark-icon {
  width: 13px;
  height: 9px;
  margin-right: 10px;
  color: rgb(@theme-colors[@primary][base]);
}

.show-search-icon {
  width: 16px;
  height: 16px;
  transition: transform 150ms ease-in-out;

  &--rotated {
    transform: rotate(135deg);
  }
}

.exercises-grid {
  display: grid;
  grid-gap: 20px;
  grid-template-columns: 1fr;

  #media-breakpoint-up(md, {
    grid-template-columns: 1fr 1fr;
  });

  #media-breakpoint-up(lg, {
    grid-template-columns: 1fr 1fr 1fr;
  });
}
</style>
